/**
 * Authenticated app shell — navy sidebar with grouped navigation, a
 * slim white top bar, and the routed content area. Rendered only when
 * AdminGate has confirmed the user is App Owner.
 */
import {
  AppShell,
  Avatar,
  Box,
  Burger,
  Button,
  Group,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBuildingStore,
  IconClipboardList,
  IconCloudUpload,
  IconDatabaseExport,
  IconInbox,
  IconLayoutDashboard,
  IconLayoutGrid,
  IconLogout,
  IconMessages,
  IconPlayerPlay,
  IconReceipt,
  IconUsers,
  IconBuilding,
} from '@tabler/icons-react';
import { Navigate, NavLink as RouterLink, Route, Routes, useLocation } from 'react-router-dom';

import { auth, signOut } from './lib/firebase';
import { shell } from './theme';

import { Dashboard } from './routes/Dashboard';
import { Studios } from './routes/Studios';
import { Users } from './routes/Users';
import { Billing } from './routes/Billing';
import { Plans } from './routes/Plans';
import { AppVersionRoute } from './routes/AppVersion';
import { MaintenanceRoute } from './routes/Maintenance';
import { OrgDetail } from './routes/OrgDetail';
import { Audit } from './routes/Audit';
import { Tutorials } from './routes/Tutorials';
import { Feedback } from './routes/Feedback';
import { IOServicesAdmin } from './routes/IOServicesAdmin';
import { IOOrdersAdmin } from './routes/IOOrdersAdmin';

type NavItem = { to: string; label: string; icon: typeof IconUsers; match?: string[] };

const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: IconLayoutDashboard }],
  },
  {
    group: 'Customers',
    items: [
      { to: '/studios', label: 'Studios', icon: IconBuilding, match: ['/studios', '/org/'] },
      { to: '/users', label: 'Users', icon: IconUsers },
    ],
  },
  {
    group: 'Billing',
    items: [
      { to: '/billing', label: 'Payments', icon: IconReceipt },
      { to: '/plans', label: 'Plan limits', icon: IconLayoutGrid },
    ],
  },
  {
    group: 'Operations',
    items: [
      { to: '/io-services', label: 'IO Services', icon: IconBuildingStore },
      { to: '/io-orders', label: 'IO Orders', icon: IconInbox },
      { to: '/feedback', label: 'Feedback', icon: IconMessages },
    ],
  },
  {
    group: 'System',
    items: [
      { to: '/app-version', label: 'App version', icon: IconCloudUpload },
      { to: '/tutorials', label: 'Tutorials', icon: IconPlayerPlay },
      { to: '/audit', label: 'Audit log', icon: IconClipboardList },
      { to: '/maintenance', label: 'Maintenance', icon: IconDatabaseExport },
    ],
  },
];

function NavEntry({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  const Icon = item.icon;
  return (
    <UnstyledButton
      component={RouterLink}
      to={item.to}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        borderRadius: 8,
        color: active ? shell.navTextActive : shell.navText,
        background: active ? shell.navActiveBg : 'transparent',
        fontWeight: active ? 600 : 500,
        fontSize: 14,
      }}
    >
      <Icon size={18} stroke={1.7} />
      {item.label}
    </UnstyledButton>
  );
}

export function App() {
  const [opened, { toggle, close }] = useDisclosure();
  const { pathname } = useLocation();
  const user = auth.currentUser;

  const isActive = (item: NavItem) =>
    (item.match ?? [item.to]).some(
      (m) => pathname === m || pathname.startsWith(m.endsWith('/') ? m : `${m}/`),
    );

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 248, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="lg"
      styles={{ main: { background: shell.canvas } }}
    >
      <AppShell.Header withBorder>
        <Group h="100%" px="lg" justify="space-between">
          <Group gap="sm">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Text fw={700} hiddenFrom="sm">
              InteriorIO Admin
            </Text>
          </Group>
          <Group gap="sm">
            {user ? (
              <Group gap={8} visibleFrom="xs">
                <Avatar src={user.photoURL} size={28} radius="xl" color="brand">
                  {(user.email ?? '?').slice(0, 1).toUpperCase()}
                </Avatar>
                <Text size="sm" c="dimmed">
                  {user.email}
                </Text>
              </Group>
            ) : null}
            <Button variant="default" size="xs" leftSection={<IconLogout size={14} />} onClick={signOut}>
              Sign out
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md" style={{ background: shell.navBg, borderRight: 'none' }}>
        <Group gap={10} px={6} mb="lg" mt={4}>
          <Box
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              background: '#2172C0',
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            IO
          </Box>
          <Stack gap={0}>
            <Text c="white" fw={700} size="sm">
              InteriorIO
            </Text>
            <Text c={shell.navGroup} size="xs">
              Admin console
            </Text>
          </Stack>
        </Group>
        <ScrollArea style={{ flex: 1 }} scrollbarSize={6}>
          <Stack gap="lg">
            {NAV.map((g) => (
              <Stack key={g.group} gap={2}>
                <Text
                  size="xs"
                  fw={700}
                  c={shell.navGroup}
                  tt="uppercase"
                  px={12}
                  mb={4}
                  style={{ letterSpacing: 0.6 }}
                >
                  {g.group}
                </Text>
                {g.items.map((item) => (
                  <NavEntry key={item.to} item={item} active={isActive(item)} onClick={close} />
                ))}
              </Stack>
            ))}
          </Stack>
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box maw={1440} mx="auto">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/studios" element={<Studios />} />
            <Route path="/studios/:id" element={<OrgDetail />} />
            <Route path="/org/:id" element={<OrgDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/io-services" element={<IOServicesAdmin />} />
            <Route path="/io-orders" element={<IOOrdersAdmin />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/app-version" element={<AppVersionRoute />} />
            <Route path="/tutorials" element={<Tutorials />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/maintenance" element={<MaintenanceRoute />} />
            {/* Old links */}
            <Route path="/subscribers" element={<Navigate to="/studios" replace />} />
            <Route path="/revenue" element={<Navigate to="/billing" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}
